import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import './styles/Landlord.css';

const Profile = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('personal');
  const [profile, setProfile] = useState({
    name: '',
    surname: '',
    email: '',
    phone: '',
    is_subscribed: false,
    is_landlord: true,
    date_joined: '',
    properties_count: 0
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('https://api.homehive.co.zw/api/user', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // Transform the response data to match our profile structure
      setProfile({
        name: response.data.user.name || '',
        surname: response.data.user.surname || '',
        email: response.data.user.email || '',
        phone: response.data.user.phone || '',
        is_subscribed: response.data.user.is_subscribed || false,
        is_landlord: response.data.user.is_landlord || false,
        date_joined: response.data.user.date_joined || '',

        properties_count: response.data.properties?.length || 0
      });
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching profile:', error);
      Swal.fire({
        title: 'Error',
        text: 'Failed to load profile data',
        icon: 'error',
        confirmButtonColor: '#1a237e'
      });
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setProfile(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const updateData = {
        name: profile.name,
        surname: profile.surname,
        phone: profile.phone,
        is_subscribed: profile.is_subscribed
      };

      await axios.put('https://api.homehive.co.zw/api/user/', updateData, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      Swal.fire({
        title: 'Success',
        text: 'Profile updated successfully',
        icon: 'success',
        confirmButtonColor: '#1a237e'
      });
      setIsEditing(false);
      fetchProfile(); // Refresh profile data
    } catch (error) {
      console.error('Error updating profile:', error);
      Swal.fire({
        title: 'Error',
        text: 'Failed to update profile',
        icon: 'error',
        confirmButtonColor: '#1a237e'
      });
    }
  };

  if (isLoading) {
    return (
      <div className="sync-loader-container">
        <div className="sync-loader">
          <div className="sync-dot"></div>
          <div className="sync-dot"></div>
          <div className="sync-dot"></div>
          <div className="sync-dot"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="fancy-profile">
      {/* Profile Header */}
      <div className="fancy-profile-header">
        <div className="header-backdrop"></div>
        <div className="landlord-header-content">
          <div className="profile-avatar-large">
            <div className="avatar-ring">
              <div className="avatar-circle">
                {profile.name && profile.surname ? (
                  <span className="initials">
                    {profile.name[0]}{profile.surname[0]}
                  </span>
                ) : (
                  <i className="fas fa-user"></i>
                )}
              </div>
            </div>
            <div className="verification-badge">
              <i className="fas fa-check-circle"></i>
            </div>
          </div>
          <div style={{marginBottom: '60px'}} className="profile-title">
            <h1>{`${profile.name} ${profile.surname}`}</h1>
            
            <button 
              className={`edit-profile-btn ${isEditing ? 'editing' : ''}`}
              onClick={() => setIsEditing(!isEditing)}
              title={isEditing ? "Cancel editing" : "Edit profile"}
            >
              <i className={`fas ${isEditing ? 'fa-times' : 'fa-edit'}`}></i>
              <span>{isEditing ? 'Cancel' : 'Edit Profile'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Profile Stats Cards */}
      <div className="stats-container">
        <div className="stat-card animated">
          <div className="stat-icon properties">
            <i className="fas fa-building"></i>
          </div>
          <div className="stat-info">
            <span className="stat-number">{profile.properties_count}</span>
            <span className="stat-label">Properties</span>
          </div>
        </div>
        <div className="stat-card animated">
          <div className="stat-icon experience">
            <i className="fas fa-star"></i>
          </div>
          <div className="stat-info">
            <span className="stat-number">
              {new Date().getFullYear() - new Date(profile.date_joined).getFullYear()}
            </span>
            <span className="stat-label">Years Experience</span>
          </div>
        </div>
        <div className="stat-card animated">
          <div className="stat-icon membership">
            <i className="fas fa-calendar-check"></i>
          </div>
          <div className="stat-info">
            <span className="stat-number">
              {new Date(profile.date_joined).toLocaleDateString('en-US', { 
                month: 'long', 
                year: 'numeric' 
              })}
            </span>
            <span className="stat-label">Member Since</span>
          </div>
        </div>
      </div>

      {/* Profile Content */}
      <div className="profile-content-wrapper">
        {/* Profile Navigation */}
        <div className="profile-nav">
          <button 
            className={`nav-tab ${activeTab === 'personal' ? 'active' : ''}`}
            onClick={() => setActiveTab('personal')}
          >
            <i className="fas fa-user"></i>
            Personal Info
          </button>
        </div>

        {/* Profile Form */}
        <div className="fancy-card">
          <div className="card-header">
            <h2>{activeTab === 'personal' ? 'Personal Information' : 'Account Settings'}</h2>
          </div>

          <form onSubmit={handleSubmit} className="fancy-form">
            {activeTab === 'personal' ? (
              <>
                <div className="form-grid">
                  <div className="form-group">
                    <label>First Name</label>
                    <div className="">
                      <input
                        type="text"
                        name="name"
                        className='fancy-input'
                        value={profile.name}
                        onChange={handleChange}
                        disabled={!isEditing}
                        required
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Last Name</label>
                    <div >
                      <input
                        type="text"
                        name="surname"
                        className="fancy-input"
                        value={profile.surname}
                        onChange={handleChange}
                        disabled={!isEditing}
                        required
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Email Address</label>
                    <div >
                      <input
                        className="fancy-input"
                        type="email"
                        name="email"
                        value={profile.email}
                        disabled
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Phone Number</label>
                    <div >
                      <input
                        className="fancy-input"
                        type="tel"
                        name="phone"
                        value={profile.phone}
                        onChange={handleChange}
                        disabled={!isEditing}
                        placeholder="Enter your phone number"
                      />
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="settings-section">
                <div className="fancy-toggle">
                  <label className="toggle-label">
                    <input
                      type="checkbox"
                      name="is_subscribed"
                      checked={profile.is_subscribed}
                      onChange={handleChange}
                      disabled={!isEditing}
                    />
                    <span className="toggle-slider"></span>
                    <span className="toggle-text">
                      <i className="fas fa-bell"></i>
                      Receive Newsletter and Updates
                    </span>
                  </label>
                </div>
                {/* Add more settings options here */}
              </div>
            )}

            {isEditing && (
              <div className="form-actions">
                <button 
                  type="button" 
                  className="btn-cancel"
                  onClick={() => setIsEditing(false)}
                >
                  <i className="fas fa-times"></i>
                  Cancel
                </button>
                <button type="submit" className="btn-save">
                  <i className="fas fa-save"></i>
                  Save Changes
                </button>
              </div>
            )}
      </form>
        </div>
      </div>
    </div>
  );
};

export default Profile; 